import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

import { useAutosave } from '../hooks/useAutosave';
import { enrichService, matcherService, coverLetterService, resumeService, atsService, getBlobErrorMessage, fromServerFormat, LAST_TEMPLATE_KEY } from '../services/api';
import { useNavGuard } from '../context/NavGuardContext';
import { isSameResume, toParsedData, convertParsedToResumeRequest } from '../utils/resumeCompare';
import { mergeImportedResume } from '../utils/resumeMerge';
import InlineEditor, { type InlineSuggestion } from './InlineEditor';
import LivePreview from './LivePreview';
import AIAssistantPanel from './AIAssistantPanel';
import AtsScoreGauge from './ats/AtsScoreGauge';
import WorkspaceHeader from './WorkspaceHeader';
import SaveCardPrompt from './SaveCardPrompt';
import { Eye, Loader2 } from 'lucide-react';
import type {
  ResumeRequest, PersonalInfo, Experience, Education, Resume,
  EnrichedData, MatchedCV, JDAnalysis as JDAnalysisType, MatchScore as MatchScoreType,
  AtsScoreResponse, ParsedData,
} from '../types';

type SectionKey = 'personal' | 'summary' | 'experience' | 'education' | 'skills';

function generateSuggestions(working: ResumeRequest, enriched: EnrichedData): InlineSuggestion[] {
  const list: InlineSuggestion[] = [];
  let id = 0;

  if (enriched.summary && enriched.summary !== (working.summary || '')) {
    list.push({
      id: `sug-${id++}`,
      section: 'summary',
      field: 'summary',
      label: 'Professional Summary',
      originalValue: working.summary || '',
      suggestedValue: enriched.summary,
      status: 'pending',
    });
  }

  enriched.experience.forEach((exp, i) => {
    const wExp = working.experience[i];
    if (!wExp) return;
    if (exp.description && exp.description !== wExp.description) {
      list.push({
        id: `sug-${id++}`,
        section: 'experience',
        field: 'description',
        label: `${exp.role} at ${exp.company}`,
        originalValue: wExp.description,
        suggestedValue: exp.description,
        status: 'pending',
        experienceIndex: i,
      });
    }
  });

  (enriched.suggestedSkills || []).forEach((skill) => {
    if (!working.skills.includes(skill)) {
      list.push({
        id: `sug-${id++}`,
        section: 'skills',
        field: 'skill',
        label: `Add skill: ${skill}`,
        originalValue: '',
        suggestedValue: skill,
        status: 'pending',
      });
    }
  });

  return list;
}

const isNotFound = (err: unknown): boolean =>
  (err as { response?: { status?: number } })?.response?.status === 404;

const Workspace: React.FC = () => {
  const { user, loginProvider } = useAuth();
  const navigate = useNavigate();
  const profileFullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const profileEmail = user?.email || '';

  // Core working data
  const defaultTemplate = 'modern';
  const [workingData, setWorkingData] = useState<ResumeRequest>({
    templateId: defaultTemplate,
    personalInfo: { fullName: '', email: '', phone: '', address: '', linkedin: '', website: '', photo: '' },
    summary: '',
    experience: [],
    education: [],
    skills: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(defaultTemplate);

  useEffect(() => {
    if (!user?.id) {
      navigate('/', { replace: true });
      return;
    }
    let cancelled = false;
    const applyProfile = (raw: ResumeRequest) => {
      const converted = fromServerFormat(raw as unknown as Record<string, unknown>);
      const bootTemplate = converted.templateId || localStorage.getItem(LAST_TEMPLATE_KEY) || defaultTemplate;
      setWorkingData({
        ...converted,
        templateId: bootTemplate,
        personalInfo: {
          ...converted.personalInfo,
          fullName: profileFullName || converted.personalInfo.fullName,
          email: profileEmail || converted.personalInfo.email,
          photo: converted.personalInfo.photo || user?.avatarUrl || '',
        },
      });
      setSelectedTemplate(bootTemplate);
    };
    (async () => {
      try {
        const response = await resumeService.getProfile(user.id);
        if (cancelled) return;
        applyProfile(response.data);
      } catch (err) {
        if (cancelled) return;
        // A brand-new LinkedIn sign-in has no workspace profile yet — import
        // the identity claims (name/email/photo) from the token and continue
        // into a prefilled resume. Otherwise fall back to the dashboard.
        if (loginProvider === 'linkedin' && isNotFound(err)) {
          try {
            const imported = await resumeService.importLinkedInProfile(user.id);
            if (cancelled) return;
            applyProfile(imported.data);
          } catch {
            if (!cancelled) navigate('/dashboard', { replace: true });
          }
        } else {
          navigate('/dashboard', { replace: true });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, loginProvider]);

  const [activeSection, setActiveSection] = useState<SectionKey>('personal');

  const handleSelectTemplate = useCallback((template: string) => {
    setSelectedTemplate(template);
    setWorkingData(prev => ({ ...prev, templateId: template }));
    localStorage.setItem(LAST_TEMPLATE_KEY, template);
    if (user?.id) {
      void resumeService
        .saveProfile(user.id, { ...workingData, templateId: template })
        .catch(() => {
          // Non-critical: the debounced autosave persists the same template shortly after.
        });
    }
  }, [user?.id, workingData]);

  // Enrichment state
  const [enrichedData, setEnrichedData] = useState<EnrichedData | null>(null);
  const [suggestions, setSuggestions] = useState<InlineSuggestion[]>([]);
  const [isEnhancing, setIsEnhancing] = useState(false);

  // Match state
  const [matchedCV, setMatchedCV] = useState<MatchedCV | null>(null);
  const [jdAnalysis, setJdAnalysis] = useState<JDAnalysisType | null>(null);
  const [matchScore, setMatchScore] = useState<MatchScoreType | null>(null);
  const [jdUrl, setJdUrl] = useState('');
  const [jdText, setJdText] = useState('');
  const [useTextMatch, setUseTextMatch] = useState(false);
  const [isMatching, setIsMatching] = useState(false);

  // ATS score state
  const [atsScore, setAtsScore] = useState<AtsScoreResponse | null>(null);
  const [isScoringAts, setIsScoringAts] = useState(false);

  // Cover letter & PDF
  const [isGeneratingCover, setIsGeneratingCover] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Responsive drawer state
  const [rightPanelOpen, setRightPanelOpen] = useState(false);

  // Leave-guard prompt state
  const [showSaveCardPrompt, setShowSaveCardPrompt] = useState(false);
  const [isSavingCard, setIsSavingCard] = useState(false);
  const { registerGuard } = useNavGuard();

  // Autosave
  const saveStatus = useAutosave(isLoading ? null : workingData);

  // Working data update helpers
  const handleUpdatePersonalInfo = useCallback((info: PersonalInfo) => {
    setWorkingData(prev => ({
      ...prev,
      personalInfo: {
        ...info,
        fullName: profileFullName || info.fullName,
        email: profileEmail || info.email,
      },
    }));
  }, [profileFullName, profileEmail]);

  const handleUpdateSummary = useCallback((summary: string) => {
    setWorkingData(prev => ({ ...prev, summary }));
  }, []);

  const handleUpdateExperience = useCallback((index: number, exp: Experience) => {
    setWorkingData(prev => {
      const experience = [...prev.experience];
      experience[index] = exp;
      return { ...prev, experience };
    });
  }, []);

  const handleAddExperience = useCallback(() => {
    setWorkingData(prev => ({
      ...prev,
      experience: [...prev.experience, {
        company: '', role: '', startDate: '', endDate: '', current: false, description: '',
      }],
    }));
    setActiveSection('experience');
  }, []);

  const handleRemoveExperience = useCallback((index: number) => {
    setWorkingData(prev => ({
      ...prev,
      experience: prev.experience.filter((_, i) => i !== index),
    }));
  }, []);

  const handleUpdateEducation = useCallback((index: number, edu: Education) => {
    setWorkingData(prev => {
      const education = [...prev.education];
      education[index] = edu;
      return { ...prev, education };
    });
  }, []);

  const handleAddEducation = useCallback(() => {
    setWorkingData(prev => ({
      ...prev,
      education: [...prev.education, {
        institution: '', degree: '', fieldOfStudy: '', year: '', gpa: '',
        startDate: '', endDate: '', current: false,
      }],
    }));
    setActiveSection('education');
  }, []);

  const handleRemoveEducation = useCallback((index: number) => {
    setWorkingData(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index),
    }));
  }, []);

  const handleUpdateSkills = useCallback((skills: string[]) => {
    setWorkingData(prev => ({ ...prev, skills }));
  }, []);

  // Import from an uploaded resume file: merge parsed experience, education,
  // skills, summary and blank personal fields into the current form.
  const handleImportResume = useCallback((parsed: ParsedData) => {
    const imported = convertParsedToResumeRequest(parsed, selectedTemplate);
    const { merged, counts } = mergeImportedResume(workingData, imported);
    setWorkingData(merged);

    const parts: string[] = [];
    if (counts.experienceAdded > 0) parts.push(`${counts.experienceAdded} experience${counts.experienceAdded > 1 ? 's' : ''}`);
    if (counts.educationAdded > 0) parts.push(`${counts.educationAdded} education${counts.educationAdded > 1 ? 's' : ''}`);
    if (counts.skillsAdded > 0) parts.push(`${counts.skillsAdded} skills`);
    if (counts.summary) parts.push('summary');
    if (counts.personalInfo > 0) parts.push(`${counts.personalInfo} profile field${counts.personalInfo > 1 ? 's' : ''}`);

    if (parts.length === 0) {
      toast('Nothing new to import from this resume');
    } else {
      toast.success(`Imported from resume: ${parts.join(', ')}`);
    }
    if (counts.experienceAdded > 0) setActiveSection('experience');
    else if (counts.educationAdded > 0) setActiveSection('education');
    else if (counts.skillsAdded > 0) setActiveSection('skills');
    else if (counts.summary) setActiveSection('summary');
  }, [workingData, selectedTemplate]);

  // Enrichment
  const handleEnhanceAll = useCallback(async () => {
    setIsEnhancing(true);
    try {
      const result = await enrichService.enrichContent(toParsedData(workingData));
      if (result.success && result.enriched) {
        setEnrichedData(result.enriched);
        const newSuggestions = generateSuggestions(workingData, result.enriched);
        setSuggestions(prev => [...prev, ...newSuggestions]);
        if (newSuggestions.length === 0) {
          toast.success('No new suggestions — your content looks great!');
        } else {
          toast.success(`${newSuggestions.length} suggestion${newSuggestions.length > 1 ? 's' : ''} ready for review`);
        }
      } else {
        toast.error(result.error || 'Enrichment failed');
      }
    } catch {
      toast.error('Failed to enhance resume');
    } finally {
      setIsEnhancing(false);
    }
  }, [workingData]);

  const handleEnrichSection = useCallback(async (_section: SectionKey) => {
    setIsEnhancing(true);
    try {
      const data = toParsedData(workingData);
      const result = await enrichService.enrichContent(data);
      if (result.success && result.enriched) {
        setEnrichedData(result.enriched);
        const newSuggestions = generateSuggestions(workingData, result.enriched);
        const filtered = _section === 'summary'
          ? newSuggestions.filter(s => s.section === 'summary')
          : _section === 'experience'
            ? newSuggestions.filter(s => s.section === 'experience')
            : _section === 'skills'
              ? newSuggestions.filter(s => s.section === 'skills')
              : [];
        if (filtered.length === 0) {
          toast.success('This section looks good already!');
        } else {
          setSuggestions(prev => [...prev, ...filtered]);
          toast.success(`${filtered.length} suggestion${filtered.length > 1 ? 's' : ''} for this section`);
        }
      } else {
        toast.error(result.error || 'Enrichment failed');
      }
    } catch {
      toast.error('Failed to enhance this section');
    } finally {
      setIsEnhancing(false);
    }
  }, [workingData]);

  // Suggestion accept/dismiss
  const handleAcceptSuggestion = useCallback((id: string) => {
    setSuggestions(prev =>
      prev.map(s => s.id === id ? { ...s, status: 'accepted' as const } : s)
    );
    const suggestion = suggestions.find(s => s.id === id);
    if (!suggestion) return;

    setWorkingData(prev => {
      switch (suggestion.section) {
        case 'summary':
          return { ...prev, summary: suggestion.suggestedValue };
        case 'experience': {
          if (suggestion.experienceIndex === undefined) return prev;
          const experience = prev.experience.map((exp, i) =>
            i === suggestion.experienceIndex
              ? { ...exp, description: suggestion.suggestedValue }
              : exp
          );
          return { ...prev, experience };
        }
        case 'skills':
          return { ...prev, skills: [...prev.skills, suggestion.suggestedValue] };
        default:
          return prev;
      }
    });
  }, [suggestions]);

  const handleDismissSuggestion = useCallback((id: string) => {
    setSuggestions(prev =>
      prev.map(s => s.id === id ? { ...s, status: 'dismissed' as const } : s)
    );
  }, []);

  // JD Match
  const handleMatch = useCallback(async () => {
    if (!enrichedData) {
      toast.error('Please enhance your resume first before matching');
      return;
    }
    if (!useTextMatch && !jdUrl.trim()) return;
    if (useTextMatch && !jdText.trim()) return;

    setIsMatching(true);
    try {
      let result;
      if (useTextMatch) {
        result = await matcherService.matchText(jdText, enrichedData);
      } else {
        result = await matcherService.match(jdUrl, enrichedData);
      }
      if (result.success && result.matchedCV && result.jdAnalysis && result.matchScore) {
        setMatchedCV(result.matchedCV);
        setJdAnalysis(result.jdAnalysis);
        setMatchScore(result.matchScore);
        toast.success('Match analysis complete!');
      } else {
        toast.error(result.error || 'Matching failed');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      const detail = err.response?.data?.detail;
      if (detail && (detail.includes('timed out') || detail.includes('Could not connect'))) {
        toast.error('Could not fetch the job posting. Try pasting the text instead.');
        setUseTextMatch(true);
      } else {
        toast.error(detail || 'Failed to match resume to job');
      }
    } finally {
      setIsMatching(false);
    }
  }, [enrichedData, jdUrl, jdText, useTextMatch]);

  // Cover letter
  const handleGenerateCoverLetter = useCallback(async () => {
    if (!enrichedData || !jdAnalysis) return;
    setIsGeneratingCover(true);
    try {
      const jdContent = useTextMatch ? jdText : jdUrl;
      const result = await coverLetterService.generate(enrichedData, jdContent);
      if (result.success && result.cover_letter) {
        const blob = new Blob([result.cover_letter], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Cover_Letter_${jdAnalysis.jobTitle || 'position'}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Cover letter downloaded!');
      } else {
        toast.error(result.error || 'Failed to generate cover letter.');
      }
    } catch {
      toast.error('Failed to generate cover letter.');
    } finally {
      setIsGeneratingCover(false);
    }
  }, [enrichedData, jdAnalysis, jdUrl, jdText, useTextMatch]);

  // PDF download
  const handleDownloadPdf = useCallback(async () => {
    setIsGeneratingPdf(true);
    try {
      const response = await resumeService.generateResume({
        ...workingData,
        templateId: selectedTemplate,
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${workingData.personalInfo?.fullName || 'Resume'}_${selectedTemplate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Resume downloaded!');
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 403) {
        toast.error('This is a Pro template. Upgrade to download it.');
      } else if (status === 503) {
        toast.error('Payments are temporarily unavailable. You can download once payments are re-enabled and you have an active subscription.');
      } else {
        toast.error(await getBlobErrorMessage(error));
      }
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [workingData, selectedTemplate]);

  // Leave-to-dashboard guard: silently leave when an identical card exists,
  // otherwise offer to save the current workspace data as a card.
  const handleLeaveToDashboard = useCallback(async () => {
    if (showSaveCardPrompt) return;
    try {
      const response = await resumeService.getAllResumes();
      const resumes: Resume[] = response.data || [];
      const card = {
        ...workingData,
        templateId: selectedTemplate,
      };
      const identical = resumes.some((saved) => isSameResume(card, saved));
      if (identical) {
        navigate('/dashboard', { replace: true });
        return;
      }
      setShowSaveCardPrompt(true);
    } catch {
      navigate('/dashboard', { replace: true });
    }
  }, [showSaveCardPrompt, workingData, selectedTemplate, navigate]);

  const handleSaveCard = useCallback(async () => {
    if (isSavingCard) return;
    setIsSavingCard(true);
    try {
      await resumeService.saveResume({
        ...workingData,
        templateId: selectedTemplate,
      });
      toast.success('Resume saved as a card!');
      setShowSaveCardPrompt(false);
      navigate('/dashboard', { replace: true });
    } catch {
      toast.error('Failed to save resume as a card.');
      setIsSavingCard(false);
    }
  }, [isSavingCard, workingData, selectedTemplate, navigate]);

  // Register the leave-guard while the workspace is mounted so Layout's
  // Dashboard links route through it (and unregister on unmount).
  useEffect(() => {
    registerGuard(handleLeaveToDashboard);
    return () => registerGuard(null);
  }, [registerGuard, handleLeaveToDashboard]);

  // ATS score
  const atsRequestSeq = useRef(0);
  const atsJobDescription = useTextMatch && jdText.trim() ? jdText.trim() : undefined;

  const handleScoreAts = useCallback(async (jobDescription?: string) => {
    const seq = ++atsRequestSeq.current;
    setIsScoringAts(true);
    try {
      const result = await atsService.score(toParsedData(workingData), jobDescription);
      if (seq === atsRequestSeq.current) setAtsScore(result);
    } catch {
      // keep previous score on failure
    } finally {
      if (seq === atsRequestSeq.current) setIsScoringAts(false);
    }
  }, [workingData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleScoreAts(atsJobDescription);
    }, 600);
    return () => clearTimeout(timer);
  }, [handleScoreAts, atsJobDescription]);

  const hasMatchResults = !!matchScore;
  const tailoringNotes = matchedCV?.tailoringNotes || [];
  const keywordMatches = matchedCV?.keywordMatches || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-3.5rem-3rem)]">
        <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem-3rem)] max-h-[calc(100vh-3.5rem-3rem)]">
      {/* Header */}
      <WorkspaceHeader
        hasMatchResults={hasMatchResults}
        isEnhancing={isEnhancing}
        isGeneratingCover={isGeneratingCover}
        isGeneratingPdf={isGeneratingPdf}
        saveStatus={saveStatus}
        onBack={handleLeaveToDashboard}
        onEnhanceAll={handleEnhanceAll}
        onGenerateCoverLetter={handleGenerateCoverLetter}
        onDownloadPdf={handleDownloadPdf}
      />

      {/* Two-panel body */}
      <div className="flex flex-1 min-h-0">
        {/* Center Panel — match UI at top, editor below */}
        <div className="w-full lg:w-1/2 min-w-0 bg-white overflow-y-auto">
          <AIAssistantPanel
            matchScore={matchScore}
            jdAnalysis={jdAnalysis}
            tailoringNotes={tailoringNotes}
            keywordMatches={keywordMatches}
            isMatching={isMatching}
            jdUrl={jdUrl}
            jdText={jdText}
            useTextMatch={useTextMatch}
            atsScore={atsScore}
            isScoringAts={isScoringAts}
            onJdUrlChange={setJdUrl}
            onJdTextChange={setJdText}
            onToggleMatchMode={() => setUseTextMatch(v => !v)}
            onMatch={handleMatch}
          />
          <InlineEditor
            workingData={workingData}
            suggestions={suggestions}
            activeSection={activeSection}
            isEnhancing={isEnhancing}
            onSetActiveSection={setActiveSection}
            onUpdatePersonalInfo={handleUpdatePersonalInfo}
            onUpdateSummary={handleUpdateSummary}
            onUpdateExperience={handleUpdateExperience}
            onAddExperience={handleAddExperience}
            onRemoveExperience={handleRemoveExperience}
            onUpdateEducation={handleUpdateEducation}
            onAddEducation={handleAddEducation}
            onRemoveEducation={handleRemoveEducation}
            onUpdateSkills={handleUpdateSkills}
            onAcceptSuggestion={handleAcceptSuggestion}
            onDismissSuggestion={handleDismissSuggestion}
            onEnrichSection={handleEnrichSection}
            onImport={handleImportResume}
          />
        </div>

        {/* Right Panel — desktop */}
        <aside className="hidden lg:flex lg:flex-col w-1/2 min-w-0 border-l border-zinc-200 bg-white overflow-hidden">
          <div className="px-4 pt-4 pb-2 shrink-0">
            <AtsScoreGauge
              score={atsScore?.score ?? 0}
              breakdown={atsScore?.breakdown}
              loading={isScoringAts}
              compact
            />
          </div>
          <div className="flex-1 min-h-0">
            <LivePreview
              data={workingData}
              selectedTemplate={selectedTemplate}
              onSelectTemplate={handleSelectTemplate}
              activeSection={activeSection}
            />
          </div>
        </aside>

        {/* Mobile FAB */}
        <div className="lg:hidden fixed bottom-6 left-1/2 -translate-x-1/2 flex gap-3 z-30">
          <button
            className="h-11 px-5 bg-white border border-zinc-200 shadow-elevated text-zinc-700 text-sm font-medium rounded-full inline-flex items-center gap-2 hover:bg-zinc-50 transition-colors"
            onClick={() => setRightPanelOpen(true)}
          >
            <Eye className="w-4 h-4 text-brand-500" />
            Preview
          </button>
        </div>
      </div>

      {/* Mobile Drawer — Right */}
      <AnimatePresence>
        {rightPanelOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="absolute inset-0 bg-black/30" onClick={() => setRightPanelOpen(false)} />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="absolute right-0 top-0 bottom-0 w-[85vw] max-w-[360px] bg-white shadow-elevated flex flex-col overflow-hidden"
            >
              <div className="px-4 pt-3 pb-2 shrink-0">
                <AtsScoreGauge
                  score={atsScore?.score ?? 0}
                  breakdown={atsScore?.breakdown}
                  loading={isScoringAts}
                  compact
                />
              </div>
              <div className="flex-1 min-h-0">
                <LivePreview
                  data={workingData}
                  selectedTemplate={selectedTemplate}
                  onSelectTemplate={handleSelectTemplate}
                  onClose={() => setRightPanelOpen(false)}
                  activeSection={activeSection}
                />
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      {showSaveCardPrompt && (
        <SaveCardPrompt
          isSaving={isSavingCard}
          onSave={handleSaveCard}
          onSkip={() => { setShowSaveCardPrompt(false); navigate('/dashboard', { replace: true }); }}
          onClose={() => setShowSaveCardPrompt(false)}
        />
      )}
    </div>
  );
};

export default Workspace;
